import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AuthLayout from '../../components/auth/AuthLayout';
import OTPInput from '../../components/auth/OTPInput';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMail, FiArrowLeft, FiArrowRight, FiRefreshCw, FiAlertCircle, FiCheckCircle, FiClock } = FiIcons;

const OTP_COUNTDOWN_SECONDS = 10 * 60; // 10 minutes

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

/**
 * Two-step login:
 *  Step 1 — request email OTP
 *  Step 2 — enter 6-digit code (auto-advancing boxes, 10-min countdown, resend)
 * The /verify-otp route renders this same component locked to step 2 when an
 * in-flight otpId is present in location.state.
 */
const Login = () => {
  const { requestEmailOTP, verifyEmailOTP, authError, clearAuthError, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [step, setStep] = useState(location.state?.otpId ? 2 : 1);
  const [email, setEmail] = useState(location.state?.email || '');
  const [otpId, setOtpId] = useState(location.state?.otpId || null);
  const [digits, setDigits] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [resentNotice, setResentNotice] = useState(false);

  // Already signed in → go straight to the workspace (or the original destination)
  useEffect(() => {
    if (isAuthenticated) {
      navigate(location.state?.from?.pathname || '/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate, location.state]);

  // 10-minute expiry countdown while on the OTP step
  useEffect(() => {
    if (step !== 2 || countdown <= 0) return undefined;
    const timer = setInterval(() => {
      setCountdown((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [step, countdown > 0]);

  const startCountdown = useCallback(() => setCountdown(OTP_COUNTDOWN_SECONDS), []);

  const handleRequestOTP = async (e) => {
    e?.preventDefault();
    setLocalError(null);
    clearAuthError();

    if (!isValidEmail(email)) {
      setLocalError('Please enter a valid work email address.');
      return;
    }

    setSubmitting(true);
    try {
      const { otpId: newOtpId } = await requestEmailOTP(email.trim().toLowerCase());
      setOtpId(newOtpId);
      setDigits([]);
      setStep(2);
      startCountdown();
    } catch (err) {
      // AuthContext has already surfaced the friendly message via authError
      console.warn('OTP request failed:', err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerify = async (code) => {
    if (!otpId) {
      setStep(1);
      return;
    }
    setLocalError(null);
    clearAuthError();
    setSubmitting(true);
    try {
      await verifyEmailOTP(otpId, code);
      // Context + redirect effect handle navigation on isAuthenticated
    } catch (err) {
      setDigits([]);
      console.warn('OTP verify failed:', err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (countdown > OTP_COUNTDOWN_SECONDS - 30) {
      // Throttle: allow resend only once the code has been live ≥30s
      return;
    }
    setLocalError(null);
    clearAuthError();
    setResending(true);
    try {
      const { otpId: newOtpId } = await requestEmailOTP(email.trim().toLowerCase());
      setOtpId(newOtpId);
      setDigits([]);
      setResentNotice(true);
      startCountdown();
      setTimeout(() => setResentNotice(false), 4000);
    } catch (err) {
      console.warn('Resend failed:', err.message);
    } finally {
      setResending(false);
    }
  };

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const errorBanner = localError || authError;

  return (
    <AuthLayout
      title={step === 1 ? 'Sign in to your workspace' : 'Enter verification code'}
      subtitle={
        step === 1
          ? 'We will email you a single-use 6-digit code — no password needed.'
          : `We sent a 6-digit code to ${email}. It expires in 10 minutes.`
      }
      footer={
        step === 1 ? (
          <span>
            New organization?{' '}
            <Link to="/signup" className="font-bold text-blue-600 dark:text-blue-400 hover:underline">
              Create an account
            </Link>
          </span>
        ) : null
      }
    >
      {errorBanner && (
        <div className="mb-4 p-3 rounded-xl bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 flex items-start gap-2.5">
          <SafeIcon icon={FiAlertCircle} className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-red-700 dark:text-red-300 leading-relaxed">{errorBanner}</p>
        </div>
      )}

      {resentNotice && (
        <div className="mb-4 p-3 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-900 flex items-start gap-2.5">
          <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-emerald-700 dark:text-emerald-300">A new code has been sent to {email}.</p>
        </div>
      )}

      {step === 1 ? (
        <form onSubmit={handleRequestOTP} className="space-y-4">
          <div>
            <label htmlFor="login-email" className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1.5">
              Work email address
            </label>
            <div className="relative">
              <SafeIcon icon={FiMail} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input
                id="login-email"
                type="email"
                autoComplete="email"
                placeholder="you@company.co.uk"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={submitting}
                className="w-full pl-10 pr-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500"
              />
            </div>
            <p className="text-[11px] text-slate-400 mt-1.5">
              Only emails registered by your organization admin can receive codes.
            </p>
          </div>

          <button
            type="submit"
            disabled={submitting || !email}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-sm font-bold shadow-md shadow-blue-900/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Sending code...' : 'Send Login Code'}
            {!submitting && <SafeIcon icon={FiArrowRight} className="w-4 h-4" />}
          </button>
        </form>
      ) : (
        <div className="space-y-5">
          <OTPInput
            value={digits}
            onChange={setDigits}
            onComplete={handleVerify}
            disabled={submitting}
            hasError={Boolean(authError)}
          />

          <button
            type="button"
            onClick={() => handleVerify(digits.join(''))}
            disabled={submitting || digits.join('').length < 6}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-sm font-bold shadow-md shadow-blue-900/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Verifying...' : 'Verify & Sign In'}
          </button>

          <div className="flex items-center justify-between text-xs">
            <span className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400">
              <SafeIcon icon={FiClock} className="w-3.5 h-3.5" />
              {countdown > 0 ? (
                <>Code expires in <span className="font-mono font-bold text-slate-700 dark:text-slate-200">{formatTime(countdown)}</span></>
              ) : (
                <span className="text-red-500 font-semibold">Code expired — request a new one</span>
              )}
            </span>

            <button
              type="button"
              onClick={handleResend}
              disabled={resending || countdown > OTP_COUNTDOWN_SECONDS - 30}
              className="flex items-center gap-1.5 font-bold text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50 disabled:no-underline disabled:cursor-not-allowed"
            >
              <SafeIcon icon={FiRefreshCw} className={`w-3.5 h-3.5 ${resending ? 'animate-spin' : ''}`} />
              {resending ? 'Resending...' : 'Resend Code'}
            </button>
          </div>

          <button
            type="button"
            onClick={() => {
              setStep(1);
              setOtpId(null);
              setDigits([]);
              setCountdown(0);
              setLocalError(null);
              clearAuthError();
            }}
            className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"
          >
            <SafeIcon icon={FiArrowLeft} className="w-3.5 h-3.5" />
            Use a different email
          </button>
        </div>
      )}
    </AuthLayout>
  );
};

export default Login;
